// HomePinnedDashboard.jsx

import React, { useEffect, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Services
import { subscribeToDashboard } from 'services/dashboards/DashboardReadService';

// CSS
import './HomePinnedDashboard.css';

// Components
import Dashboard from 'dashboard/Dashboard';

const HomePinnedDashboard = React.memo(({ dashboardKey }) => {
  // ----- CONTEXT ----- //

  const { theme } = useTheme();
  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();
  // ----- STATE ----- //

  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const cleanDashboardKey = String(dashboardKey || '').trim();

  // ----- EFFECTS ----- //

  useEffect(() => {
    if (!cleanDashboardKey) {
      setDashboard(null);
      setError('');
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError('');

    const unsubscribe = subscribeToDashboard(cleanDashboardKey, (row) => {
      if (cancelled) return;

      if (row) {
        setDashboard(row);
        setError('');
      } else {
        setDashboard(null);
        setError('Dashboard not found');
      }

      setLoading(false);
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [cleanDashboardKey]);

  if (!cleanDashboardKey) return null;

  const showLoading = loading || (!dashboard && !error);
  const showError = !loading && error && (!dashboard || dashboard.key !== cleanDashboardKey);

  // ----- RENDER ----- //

  return (
    <div className="home-layout__dashboard">
      {dashboard ? (
        <Dashboard dashboard={dashboard} showHeader publishShellChrome={false} />
      ) : null}

      {showLoading ? (
        <div
          style={{
            color: theme.foregroundMuted,
            fontSize: 14,
            opacity: 0.5,
            padding: 24,
            paddingLeft: isMobile ? 70 : 24,
          }}
        >
          Loading dashboard…
        </div>
      ) : null}

      {showError ? (
        <div
          style={{
            color: theme.foregroundMuted,
            fontSize: 12,
            padding: 30,
            paddingLeft: isMobile ? 70 : 30,
          }}
        >
          {error}
        </div>
      ) : null}
    </div>
  );
});

// Dev Tools
HomePinnedDashboard.displayName = 'HomePinnedDashboard';

export default HomePinnedDashboard;
